/**
 * Bookkeeping for Prev/Next over a cursor-paginated API.
 *
 * A cursor API only ever hands out the way *forward*: each page carries the
 * cursor that opens the page after it, and nothing that opens the page before
 * it. Stepping back therefore means remembering where we have been — the
 * "trail" is the cursor that opened each page visited so far, with `null` in
 * front because the first page opens with no cursor at all.
 *
 * Pure, so the paging rules can be tested without a component or a network.
 */

/** The trail a freshly loaded (or reloaded) list starts from: page 1, no cursor. */
export const initialTrail = () => ({ trail: [null], pageIndex: 0 });

/**
 * The trail after moving forward onto the page opened by `cursor`.
 *
 * The trail is truncated at the current page before `cursor` is appended: after
 * stepping back and then forward again, the cursors of the pages abandoned in
 * between must not be left dangling past the new position.
 */
export const pushPage = (trail, pageIndex, cursor) => ({
  trail: [...trail.slice(0, pageIndex + 1), cursor],
  pageIndex: pageIndex + 1,
});

/** The cursor that reopens the previous page, or undefined at page 1. */
export const prevCursor = (trail, pageIndex) =>
  pageIndex === 0 ? undefined : trail[pageIndex - 1];

/** Prev is live once there is a page behind us and no request in flight. */
export const canGoPrev = (pageIndex, loading) => pageIndex > 0 && !loading;

/**
 * Next is live only while the API is still handing back a cursor. A page may
 * come back short and still have more behind it, so the item count is never
 * what decides this — only the presence of a cursor is.
 */
export const canGoNext = (nextCursor, loading) => Boolean(nextCursor) && !loading;

/** Read the next cursor off a response under either spelling; null when absent. */
export const readNextCursor = (data) =>
  data?.nextCursor ?? data?.next_cursor ?? null;
